import { Plugin, PluginKey } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";
import { Editor } from "@tiptap/core";
import { Instance as TippyInstance } from "tippy.js";

import { SuggestionState, Suggestion } from "./types";
import { getContextData } from "./contextRegistry";
import { insertSuggestion, getNamespaceSuggestions, getRelevantProperties } from "./suggestionHandlers";
import {
    createPopupReferenceClientRect,
    createPopupRenderer,
    showSuggestionsPopup,
    SuggestionPopupRenderer,
} from "./ui";

export const suggestionPluginKey = new PluginKey("token-suggestion");
const tokenStartPattern = /\{([^}]*)$/;

export const createSuggestionPlugin = (editor: Editor) => {
    let popup: TippyInstance | null = null;
    let popupRenderer: SuggestionPopupRenderer | null = null;

    const destroyPopup = () => {
        const currentPopup = popup;
        const currentPopupRenderer = popupRenderer;

        popup = null;
        popupRenderer = null;

        currentPopup?.destroy();
        currentPopupRenderer?.destroy();
    };

    const getSuggestionPathDetails = (suggestion: Suggestion) => {
        const [namespace = "", ...propertySegments] = suggestion.text.split(".");

        return {
            namespace,
            propertyPath: propertySegments.join("."),
        };
    };

    const shouldAppendDot = (pluginState: SuggestionState, suggestion: Suggestion) => {
        if (!pluginState.isNamespaceSelected || !pluginState.query.endsWith(".")) {
            return false;
        }

        const { namespace, propertyPath } = getSuggestionPathDetails(suggestion);

        return propertyPath !== "" && getRelevantProperties(namespace, propertyPath, true).length > 0;
    };

    const shouldKeepPopupOpen = (pluginState: SuggestionState, suggestion: Suggestion) => {
        if (!pluginState.isNamespaceSelected) {
            return (
                Boolean(getContextData(suggestion.text)) && getRelevantProperties(suggestion.text, "", true).length > 0
            );
        }

        return shouldAppendDot(pluginState, suggestion);
    };

    const handleInsert = (suggestion: Suggestion) => {
        const pluginState = suggestionPluginKey.getState(editor.view.state);
        if (!pluginState) return;

        const { isNamespaceSelected, range } = pluginState;
        const keepPopupOpen = shouldKeepPopupOpen(pluginState, suggestion);

        insertSuggestion(suggestion, isNamespaceSelected, editor, range, shouldAppendDot(pluginState, suggestion));

        if (popup && !keepPopupOpen) {
            popup.hide();
        }
    };

    return new Plugin({
        key: suggestionPluginKey,
        state: {
            init(): SuggestionState {
                return {
                    active: false,
                    range: null,
                    query: "",
                    text: "",
                    suggestions: [],
                    selectedIndex: 0,
                    isNamespaceSelected: false,
                };
            },
            apply(transaction, state: SuggestionState): SuggestionState {
                const meta = transaction.getMeta(suggestionPluginKey);
                const shouldHide = transaction.getMeta("token-suggestion-hide");

                if (shouldHide) {
                    return { ...state, active: false };
                }

                if (meta) {
                    return { ...state, ...meta };
                }

                if (!transaction.docChanged && !transaction.selectionSet) {
                    return state;
                }

                const { selection } = transaction;
                const { $from, from } = selection;

                const text = $from.doc.textBetween(Math.max(0, from - 100), from, "\n");
                const tokenStartMatch = tokenStartPattern.exec(text);

                if (!tokenStartMatch) {
                    return { ...state, active: false };
                }

                const query = tokenStartMatch[1] || "";
                const range = { from: from - query.length - 1, to: from };

                const parts = query.split(".");
                const namespace = parts[0];
                const justTypedDot = query.endsWith(".");

                let propertyPath = "";
                if (parts.length > 1) {
                    propertyPath = justTypedDot ? parts.slice(1, -1).join(".") : parts.slice(1).join(".");
                }

                let suggestions: Suggestion[];
                let isNamespaceSelected = false;

                if (namespace && getContextData(namespace) && (justTypedDot || parts.length > 1)) {
                    const relevantProperties = getRelevantProperties(namespace, propertyPath, justTypedDot);
                    const namespaceData = getContextData(namespace);
                    suggestions = relevantProperties.map((prop) => {
                        const completePath =
                            justTypedDot ?
                                propertyPath ? `${propertyPath}.${prop.name}`
                                :   prop.name
                            :   prop.name;

                        const fullPath =
                            justTypedDot || !propertyPath.includes(".") ?
                                completePath
                            :   `${propertyPath.substring(0, propertyPath.lastIndexOf("."))}.${prop.name}`;

                        const pathMetadata = namespaceData.find((metadata) => metadata.path === fullPath);

                        return {
                            text: `${namespace}.${completePath}`,
                            displayText: prop.displayText ?? (prop.isArray ? `${prop.name}[]` : prop.name),
                            insertSegment: prop.name,
                            isArray: prop.isArray,
                            description: prop.description ?? pathMetadata?.description,
                        };
                    });
                    isNamespaceSelected = true;
                } else {
                    suggestions = getNamespaceSuggestions(query);
                }

                return {
                    active: suggestions.length > 0,
                    range,
                    query,
                    text,
                    suggestions,
                    selectedIndex: 0,
                    isNamespaceSelected,
                };
            },
        },
        props: {
            handleKeyDown(view, event) {
                const pluginState = suggestionPluginKey.getState(view.state);
                if (!pluginState?.active || !pluginState.suggestions.length) return false;

                const { suggestions, selectedIndex } = pluginState;

                if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                    const maxIndex = suggestions.length - 1;

                    let newIndex;
                    if (event.key === "ArrowDown") {
                        newIndex = selectedIndex >= maxIndex ? 0 : selectedIndex + 1;
                    } else {
                        newIndex = selectedIndex <= 0 ? maxIndex : selectedIndex - 1;
                    }

                    view.dispatch(
                        view.state.tr.setMeta(suggestionPluginKey, { ...pluginState, selectedIndex: newIndex }),
                    );
                    return true;
                }

                if (event.key === "Enter" || event.key === "Tab") {
                    if (selectedIndex < suggestions.length) {
                        handleInsert(suggestions[selectedIndex]);
                        return true;
                    }
                    return false;
                }

                if (event.key === "Escape") {
                    if (popup) popup.hide();
                    view.dispatch(view.state.tr.setMeta(suggestionPluginKey, { ...pluginState, active: false }));
                    return true;
                }

                if (event.key === "i") {
                    return false;
                }

                return false;
            },
            decorations(state) {
                const { active, range } = suggestionPluginKey.getState(state) || {};
                if (!active || !range) return null;
                return DecorationSet.create(state.doc, [
                    Decoration.widget(range.from, () => document.createElement("span")),
                ]);
            },
        },
        view() {
            return {
                update(view, prevState) {
                    const state = suggestionPluginKey.getState(view.state);
                    const prev = suggestionPluginKey.getState(prevState);

                    if (!state) return;

                    if (view.state.tr.getMeta("token-suggestion-hide")) {
                        destroyPopup();
                        return;
                    }

                    const isQueryChanged = prev?.query !== state.query;
                    const isSuggestionsChanged = prev?.suggestions?.length !== state.suggestions?.length;
                    const isActiveChanged = prev?.active !== state.active;
                    const isSelectionChanged = prev?.selectedIndex !== state.selectedIndex;

                    if (!state.active) {
                        destroyPopup();
                        return;
                    }

                    if (isQueryChanged || isSuggestionsChanged || isActiveChanged || isSelectionChanged || !popup) {
                        if (!popupRenderer) {
                            popupRenderer = createPopupRenderer(state, handleInsert);
                        } else {
                            popupRenderer.render(state, handleInsert);
                        }

                        if (popup) {
                            popup.setProps({
                                getReferenceClientRect: createPopupReferenceClientRect(view, state.range?.from ?? 0),
                            });
                            popup.show();
                            popup.popperInstance?.update();
                        } else {
                            popup = showSuggestionsPopup(view, state.range?.from ?? 0, popupRenderer.container);
                        }
                    }
                },
                destroy() {
                    destroyPopup();
                },
            };
        },
    });
};
